import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import SegmentNavigator from '@/components/SegmentNavigator';
import TimerChip from '@/components/TimerChip';
import StuckCard from '@/components/StuckCard';
import { useProgress } from '@/hooks/useProgress';
import Segment1 from '@/content/Segment1';
import Segment2 from '@/content/Segment2';
import Segment3 from '@/content/Segment3';
import Segment4 from '@/content/Segment4';
import Segment5 from '@/content/Segment5';
import Segment6 from '@/content/Segment6';
import Segment7 from '@/content/Segment7';
import Segment8 from '@/content/Segment8';
import Segment9 from '@/content/Segment9';

const COMPONENTS = { 1: Segment1, 2: Segment2, 3: Segment3, 4: Segment4, 5: Segment5, 6: Segment6, 7: Segment7, 8: Segment8, 9: Segment9 };

const META = {
  1: { title: 'Base Camp',             timerPreset: 13 * 60,       stuckTips: null },
  2: { title: 'Trail Markers',         timerPreset: 10 * 60,       stuckTips: null },
  3: { title: 'The Briefing',          timerPreset: 10 * 60,       stuckTips: null },
  4: {
    title: 'Gear Check', timerPreset: 8 * 60,
    stuckTips: [
      "Workspace not loading? Open claude.ai — free tier works right now",
      "No invite? Text your steward — redemption codes are standing by",
      "Try incognito mode or a different browser",
    ],
  },
  5: {
    title: 'Prompt School', timerPreset: 34 * 60,
    stuckTips: [
      "Blank page? Fill the scaffold, don't write from scratch",
      "Output too generic? Add one real detail to Context and regenerate",
      "Tool hiccup? Your mentor first, stage only at transitions",
    ],
  },
  6: {
    title: 'The Build', timerPreset: 46 * 60,
    stuckTips: [
      "Scope too big? Automate ONE link in the chain",
      "Error message? Paste it back to Claude — that loop IS the skill",
      "Not landing by 1:15? Switch to the Org Brain — 15 minutes, guaranteed real thing",
    ],
  },
  7: { title: 'The Exchange',          timerPreset: 6 * 60,        stuckTips: null },
  8: { title: 'Show & Tell',           timerPreset: 2 * 60 + 30,   stuckTips: null },
  9: { title: 'The Sustainable Stack', timerPreset: 6 * 60,        stuckTips: null },
};

const TITLES = Object.fromEntries(Object.entries(META).map(([id, m]) => [Number(id), m.title]));

const SessionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const sessionId = parseInt(id, 10);

  const {
    isCompleted, isVisited, markComplete, markIncomplete, markVisited,
  } = useProgress();

  // Auto-mark visited on mount / segment change
  useEffect(() => {
    if (sessionId >= 1 && sessionId <= 9) markVisited(sessionId);
  }, [sessionId, markVisited]);

  const meta = META[sessionId];
  const SegmentContent = COMPONENTS[sessionId];

  if (!meta || !SegmentContent) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="text-ink-secondary mb-4">Session not found.</p>
        <Link to="/" className="text-brand-orange font-semibold">← Back home</Link>
      </div>
    );
  }

  const completed = isCompleted(sessionId);
  const prevId = sessionId > 1 ? sessionId - 1 : null;
  const nextId = sessionId < 9 ? sessionId + 1 : null;
  const hasStuck = Boolean(meta.stuckTips);

  const subHeaderStyle = { backgroundColor: 'rgba(250,246,240,0.97)', backdropFilter: 'blur(8px)' };
  const footerStyle    = { backgroundColor: 'rgba(250,246,240,0.97)', backdropFilter: 'blur(8px)' };

  return (
    <div className="pb-24" data-testid="session-page">

      {/* ── Sticky sub-header: Navigator + Timer ── */}
      <div
        className="sticky top-14 z-40 border-b border-black/[0.06]"
        style={subHeaderStyle}
        data-testid="session-subheader"
      >
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <SegmentNavigator
            currentId={sessionId}
            isCompleted={isCompleted}
            isVisited={isVisited}
          />
          {/* key resets the timer whenever the segment changes */}
          <TimerChip key={sessionId} preset={meta.timerPreset} />
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-5xl mx-auto px-6 py-10">

        {/* Breadcrumb */}
        <nav
          className="flex items-center gap-1.5 text-sm text-ink-secondary mb-10"
          data-testid="session-breadcrumb"
        >
          <Link to="/" className="hover:text-ink transition-colors">AI Lab</Link>
          <ChevronRight size={13} className="text-ink-muted shrink-0" />
          <span className="text-ink-muted">Session</span>
          <ChevronRight size={13} className="text-ink-muted shrink-0" />
          <span className="text-ink font-medium">{meta.title}</span>
        </nav>

        {/* Two-column layout when stuck card present */}
        <div className={`flex gap-12 items-start ${hasStuck ? 'lg:flex-row' : 'flex-col'}`}>

          {/* Main content column */}
          <div className="flex-1 min-w-0">
            <SegmentContent />

            {/* Inline stuck card on mobile */}
            {hasStuck && (
              <div className="lg:hidden mt-10">
                <StuckCard tips={meta.stuckTips} />
              </div>
            )}

            {/* Mark done */}
            <div
              className="mt-14 pt-8 border-t border-black/[0.06] flex justify-end"
              data-testid="mark-done-section"
            >
              <button
                data-testid="session-mark-done-btn"
                onClick={() => completed ? markIncomplete(sessionId) : markComplete(sessionId)}
                className={`inline-flex items-center gap-2 text-sm font-semibold transition-colors ${
                  completed
                    ? 'text-brand-teal'
                    : 'text-ink-secondary hover:text-brand-teal'
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    completed ? 'bg-brand-teal border-brand-teal' : 'border-current'
                  }`}
                >
                  {completed && <Check size={11} className="text-white" strokeWidth={3} />}
                </span>
                {completed ? 'Marked done ✓' : 'Mark done ✓'}
              </button>
            </div>
          </div>

          {/* Stuck card sidebar — desktop only */}
          {hasStuck && (
            <aside
              className="hidden lg:block w-60 shrink-0 sticky"
              style={{ top: '9rem' }}
              data-testid="stuck-card-sidebar"
            >
              <StuckCard tips={meta.stuckTips} />
            </aside>
          )}
        </div>
      </div>

      {/* ── Sticky footer: Prev / Next ── */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-black/[0.06]"
        style={footerStyle}
        data-testid="session-sticky-footer"
      >
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          {/* Previous */}
          {prevId ? (
            <button
              data-testid="session-prev-btn"
              onClick={() => navigate(`/session/${prevId}`)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-ink/20 text-ink text-sm font-medium hover:border-ink/50 transition-colors"
            >
              ← {TITLES[prevId]}
            </button>
          ) : (
            <Link
              to="/"
              data-testid="session-back-home-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-ink/20 text-ink text-sm font-medium hover:border-ink/50 transition-colors"
            >
              ← Home
            </Link>
          )}

          {/* Next */}
          {nextId ? (
            <button
              data-testid="session-next-btn"
              onClick={() => navigate(`/session/${nextId}`)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-orange text-white text-sm font-semibold hover:bg-brand-orange-hover transition-colors"
            >
              {TITLES[nextId]} →
            </button>
          ) : (
            <Link
              to="/"
              data-testid="session-finish-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-teal text-white text-sm font-semibold hover:bg-brand-teal/80 transition-colors"
            >
              Finish ✓
            </Link>
          )}
        </div>
      </div>

    </div>
  );
};

export default SessionPage;
